import React from 'react';
import some from 'lodash/some';
import pt from 'prop-types';

import { objectToArray, locationWithQuery, classNames } from 'utils';

import Link from 'components/link';
import Icon from 'components/icon';
import AppIcon from 'components/apps/icon';
import ViewPreferencesMenu from 'components/view/preferences-menu';

import './tabs.scss';

export default class Tabs extends React.PureComponent {
  static contextTypes = {
    clientTabs: pt.array,
    swellTabs: pt.array,
    cleanSaveTab: pt.func,
  };

  static propTypes = {
    items: pt.oneOfType([pt.array, pt.object]).isRequired,
    active: pt.string,
    className: pt.string,
    location: pt.object,
    router: pt.object,
    onClick: pt.func,
    withPreferences: pt.bool,
    overflow: pt.bool,
    overflowProps: pt.object,
  };

  constructor(props) {
    super(props);

    /** @type {React.RefObject<HTMLDivElement | null>} */
    this.tabContainerRef = React.createRef();
    /** @type {React.RefObject<HTMLUListElement | null>} */
    this.tabsHiddenRef = React.createRef();

    this.state = {
      ...this.getTabState(props),
    };

    this.buttonChildren = <Icon fa="ellipsis-h" />;
  }

  componentDidMount() {
    window.addEventListener('resize', this.onContainerResize);
    this.onContainerResize();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onContainerResize);
  }

  componentDidUpdate(prevProps) {
    const { items, withPreferences } = this.props;
    if (
      prevProps.items !== items ||
      prevProps.active !== this.props.active ||
      prevProps.withPreferences !== withPreferences
    ) {
      this.setState({ ...this.getTabState(this.props) }, this.remeasureTabs);
    }
  }

  // Remeasure tabs if new tab is added or removed to place tabs properly
  remeasureTabs = () => {
    const nextState = this.getTabState(this.props);

    if (
      nextState.items.length !== this.state.items.length ||
      nextState.moreItems.length !== this.state.moreItems.length
    ) {
      this.setState(nextState);
    }
  };

  getTabState(props) {
    const allItems = objectToArray(props.items);
    const visibleItems = allItems.filter((tab) => !tab.hidden);

    let numVisible = this.getNumVisibleItems();

    let items = visibleItems.slice(0, numVisible);

    let activeOverflowItem;
    if (numVisible < visibleItems.length) {
      activeOverflowItem = visibleItems
        .slice(numVisible)
        .find((tab) => this.isActive(tab, tab.id));

      if (activeOverflowItem) {
        numVisible = this.getNumVisibleItems(activeOverflowItem);
        items = visibleItems.slice(0, numVisible);
        items.push(activeOverflowItem);
      }
    }

    const moreItems = [
      ...visibleItems
        .slice(numVisible)
        .filter((item) => item !== activeOverflowItem),
      ...allItems.filter((tab) => tab.hidden),
    ];

    return {
      items,
      moreItems,
      allItems,
    };
  }

  getNumVisibleItems(activeOverflowItem = undefined) {
    const { overflow = false } = this.props;

    // Calculate how many tabs can fit in the container without overflowing
    const element = this.tabContainerRef.current;
    if (!element || !overflow) {
      return 100;
    }

    let numVisible = 0;

    const containerWidth = element.clientWidth;
    const itemElements = Array.from(this.tabsHiddenRef.current.children);
    // Each tab also takes the list's gap before the next item
    const gap =
      parseFloat(
        window.getComputedStyle(this.tabsHiddenRef.current).columnGap,
      ) || 0;

    let tabsWidthTotal = 200; // starting aprox. width of the "more items"
    if (activeOverflowItem) {
      const activeOverflowChild = itemElements.find(
        (child) => child.dataset.tabId === activeOverflowItem.id,
      );
      tabsWidthTotal += (activeOverflowChild?.offsetWidth || 0) + gap;
    }

    for (const tab of itemElements) {
      tabsWidthTotal += tab.offsetWidth + gap;
      if (containerWidth > tabsWidthTotal) {
        numVisible += 1;
      }
    }

    // Without hidden tabs, "more items" only shows when a tab overflows, so
    // show every tab when they fit in its room too (aprox. 80 of the 200)
    if (
      !objectToArray(this.props.items).some((tab) => tab.hidden) &&
      containerWidth > tabsWidthTotal - 80
    ) {
      return itemElements.length;
    }

    return numVisible;
  }

  onContainerResize = () => {
    const { overflow = false } = this.props;

    if (!overflow) {
      return;
    }

    this.remeasureTabs();
  };

  onClick = (event, tab) => {
    const { onClick } = this.props;
    if (onClick) {
      onClick(tab);
      event.preventDefault();
    }
  };

  isActive = (tab, key) => {
    const { items, active, location } = this.props;
    if (tab.link) {
      if (location.pathname.endsWith(tab.link)) {
        return true;
      }
      if (key === 'default') {
        const anyActive = some(items, (tab) =>
          location.pathname.endsWith(tab.link),
        );
        if (!anyActive) {
          return true;
        }
      }
      return false;
    }
    return active === key || (!active && key === 'default');
  };

  onClickTabItem = (item) => {
    const { onClick, router, location } = this.props;
    if (onClick) {
      onClick(item.id);
    }
    if (router && location) {
      router.push(
        locationWithQuery(location, {
          tab: item.id === 'default' ? undefined : item.id,
        }),
      );
    }
  };

  renderPreferencesMenu() {
    const { items, withPreferences } = this.props;
    const { clientTabs, swellTabs, cleanSaveTab } = this.context;

    if (!withPreferences) {
      return null;
    }

    return (
      <div className="tabs-preferences">
        <ViewPreferencesMenu
          sideOffset={10}
          alignOffset={-2}
          buttonChildren={this.buttonChildren}
          preferenceKey="tabs"
          items={items}
          clientItems={clientTabs}
          swellItems={swellTabs}
          cleanSaveItem={cleanSaveTab}
          onClickItem={this.onClickTabItem}
        />
      </div>
    );
  }

  renderTab(tab, key) {
    const { location } = this.props;
    const id = tab.id || key;

    return (
      <li
        key={id}
        className={this.isActive(tab, id) ? 'active' : ''}
        data-tab-id={id}
        data-tab-name={tab.label}
      >
        <Link
          to={
            tab.link ||
            (location &&
              locationWithQuery(location, {
                tab: id === 'default' ? undefined : id,
              }))
          }
          onClick={(event) =>
            this.onClick(event, id === 'default' ? undefined : id)
          }
        >
          {tab.app && (
            <AppIcon image={tab.app.logo_icon} name={tab.app.name} size={16} />
          )}
          {tab.label}
        </Link>
      </li>
    );
  }

  renderMoreTabs() {
    const { overflow = false, overflowProps } = this.props;
    const { allItems, moreItems } = this.state;
    const { cleanSaveTab } = this.context;

    if (!overflow) {
      return null;
    }

    return (
      <>
        {moreItems.length > 0 && (
          <li className="tabs-more">
            <ViewPreferencesMenu
              listOnly={true}
              {...overflowProps}
              buttonClassName="preferences-menu-trigger"
              preferenceKey="tabs"
              buttonChildren={<>{moreItems.length} more…</>}
              items={allItems}
              filter={(item) => moreItems.find((mi) => mi.id === item.id)}
              cleanSaveItem={cleanSaveTab}
              onClickItem={this.onClickTabItem}
            />
          </li>
        )}
      </>
    );
  }

  render() {
    const { className } = this.props;
    const { items, allItems } = this.state;

    return (
      <div className="tabs-container" ref={this.tabContainerRef}>
        {/* Render all visible tabs for measuring visible size of items */}
        <ul className="tabs invisible" ref={this.tabsHiddenRef}>
          {allItems.map((tab, key) => {
            if (!tab || tab.hidden) return null;
            return this.renderTab(tab, key);
          })}
        </ul>
        <ul className={classNames('tabs', className)}>
          {items.map((tab, key) => {
            if (!tab || tab.hidden) return null;
            return this.renderTab(tab, key);
          })}
          {this.renderMoreTabs()}
        </ul>
        {this.renderPreferencesMenu()}
      </div>
    );
  }
}
